import { useEffect, useState } from "react";
import {
  deletePrintConfig,
  fetchPrintConfig,
  postPrintConfig,
  updatePrintConfig,
} from "../api/printConfig";

import { PrintConfig } from "../types";
import { DEFAULT_PRINT_CONFIG } from "../printConfig.constants";

export interface PrintLayout {
  printConfig: PrintConfig;
  orgId: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  _id: {
    $oid: string;
  };
  printType: string;
}

export const defaultConfig: PrintLayout = {
  printConfig: DEFAULT_PRINT_CONFIG,
  orgId: "",
  isDeleted: false,
  createdAt: "",
  updatedAt: "",
  _id: {
    $oid: "default",
  },
  printType: "default",
};

const usePrintConfig = () => {
  const [printConfig, setPrintConfig] = useState<PrintLayout[]>([]);

  // ============================================================
  // GET PRINT CONFIG
  // ============================================================

  const getPrintConfig = async () => {
    try {
      const res = await fetchPrintConfig();

      const getData: PrintLayout[] = Array.isArray(res.data)
        ? res.data
        : [];

      setPrintConfig([
        defaultConfig,
        ...getData,
      ]);
    } catch (error) {
      console.error(
        "Failed to fetch print config:",
        error
      );
    }
  };

  useEffect(() => {
    getPrintConfig();
  }, []);

  // ============================================================
  // CREATE / UPDATE PRINT LAYOUT
  // ============================================================

  const postLayout = async (layout: {
    id?: string | null;
    printType: string;
    printConfig: PrintConfig;
  }) => {
    try {
      // ----------------------------------------------------
      // EDIT EXISTING LAYOUT
      // ----------------------------------------------------

      if (layout.id) {
        await updatePrintConfig(layout.id, {
          printType: layout.printType,
          printConfig: layout.printConfig,
        });

        console.log("Print layout updated successfully");
      }

      // ----------------------------------------------------
      // CREATE NEW LAYOUT
      // ----------------------------------------------------

      else {
        await postPrintConfig({
          printType: layout.printType,
          printConfig: layout.printConfig,
        });

        console.log("Print layout created successfully");
      }

      // Refresh list
      await getPrintConfig();

    } catch (error) {
      console.error(
        "Failed to save print config:",
        error
      );

      throw error;
    }
  };

  // ============================================================
  // DELETE PRINT LAYOUT
  // ============================================================

  const deleteLayout = async (id: string) => {
    if (!id || id === "default") {
      return;
    }

    try {
      await deletePrintConfig(id);

      await getPrintConfig();

    } catch (error) {
      console.error(
        "Failed to delete print config:",
        error
      );

      throw error;
    }
  };

  return {
    printConfig,
    postLayout,
    deleteLayout,
    getPrintConfig,
  };
};

export default usePrintConfig;